import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  IconButton,
  Button,
  Chip,
  Divider,
  FormControl,
  MenuItem,
  Pagination,
  Select,
} from '@mui/material';
import {
  History as HistoryIcon,
  Language as DomainIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  ClearAll as ClearAllIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];

const readStoredPageSize = (title, fallback) => {
  try {
    const stored = Number(window.localStorage.getItem(`searchHistoryPageSize:${title}`));
    if (PAGE_SIZE_OPTIONS.includes(stored)) return stored;
  } catch {
    // Ignore storage failures and use the default page size.
  }
  return fallback;
};

const phoneFromRecord = (record) => {
  if (!record) return '';
  const candidates = [
    record.Telefoonnummer,
    record['Telefoonnummer Mobiel'],
    record.Phone,
    record.phone,
  ];
  for (const value of candidates) {
    const text = String(value || '').trim();
    if (text && !['-', 'n/a', 'na', 'none', 'null'].includes(text.toLowerCase())) {
      return text;
    }
  }
  return '';
};

const formatRelativeTime = (isoDate) => {
  try {
    return formatDistanceToNow(new Date(isoDate), { addSuffix: true });
  } catch {
    return '';
  }
};

const SearchHistoryPanel = ({
  title,
  items,
  type,
  onSelect,
  onRemove,
  onClear,
  showWhenEmpty = false,
  emptyMessage,
  pageSize,
}) => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => (
    pageSize ? readStoredPageSize(title, pageSize) : pageSize
  ));

  const orderedItems = useMemo(() => {
    if (!items?.length) return [];
    if (!pageSize) return items;
    return [...items].sort((a, b) => {
      const toTime = (value) => {
        const parsed = value ? new Date(value).getTime() : 0;
        return Number.isNaN(parsed) ? 0 : parsed;
      };
      return toTime(b.searchedAt) - toTime(a.searchedAt);
    });
  }, [items, pageSize]);

  const effectivePageSize = pageSize ? rowsPerPage : null;
  const totalPages = effectivePageSize ? Math.max(1, Math.ceil(orderedItems.length / effectivePageSize)) : 1;
  const currentPage = Math.min(page, totalPages);
  const startIndex = effectivePageSize ? (currentPage - 1) * effectivePageSize : 0;
  const visibleItems = effectivePageSize
    ? orderedItems.slice(startIndex, startIndex + effectivePageSize)
    : orderedItems;
  const hasItems = orderedItems.length > 0;
  const newestId = orderedItems[0]?.id;

  useEffect(() => {
    setPage(1);
  }, [newestId]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const handlePageSizeChange = (event) => {
    const next = Number(event.target.value);
    setRowsPerPage(next);
    setPage(1);
    try {
      window.localStorage.setItem(`searchHistoryPageSize:${title}`, String(next));
    } catch {
      // The choice still applies for this visit when storage is unavailable.
    }
  };

  const handleClearAll = () => {
    const label = (title || 'recent searches').toLowerCase();
    const coversOtherPages = Boolean(effectivePageSize && totalPages > 1);
    const confirmed = window.confirm(
      coversOtherPages
        ? `Clear all ${label}? This removes every saved search, not only the ones on this page.`
        : `Clear all ${label}? This cannot be undone.`
    );
    if (confirmed) {
      onClear?.();
    }
  };

  if (!hasItems && !showWhenEmpty) {
    return null;
  }

  if (!hasItems && showWhenEmpty) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="subtitle2"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}
        >
          <HistoryIcon fontSize="small" color="action" />
          {title}
        </Typography>
        <Box
          sx={{
            mt: 1,
            py: 2,
            px: 2,
            bgcolor: 'action.hover',
            borderRadius: 1,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="text.secondary" align="center">
            {emptyMessage || 'No searches yet'}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography
          variant="subtitle2"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 600 }}
        >
          <HistoryIcon fontSize="small" color="action" />
          {title}
        </Typography>
        <Button
          size="small"
          onClick={handleClearAll}
          startIcon={<ClearAllIcon />}
          aria-label={`Clear all ${title}`}
          sx={{
            textTransform: 'none',
            color: 'text.secondary',
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          Clear all
        </Button>
      </Box>

      <List
        dense
        disablePadding
        sx={{
          bgcolor: 'action.hover',
          borderRadius: 1,
          border: 1,
          borderColor: 'divider',
        }}
      >
        {visibleItems.map((item, index) => {
          const phone = type === 'domain' ? (item.phone || phoneFromRecord(item.record)) : '';
          return (
          <React.Fragment key={item.id}>
            {index > 0 && <Divider component="li" />}
            <ListItem
              disablePadding
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove(item.id);
                  }}
                  aria-label="Remove from history"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              }
            >
              <ListItemButton onClick={() => onSelect(item)} sx={{ pr: 6 }}>
                {type === 'domain' ? (
                  <DomainIcon fontSize="small" sx={{ mr: 1.5, color: 'primary.main', flexShrink: 0 }} />
                ) : (
                  <PersonIcon fontSize="small" sx={{ mr: 1.5, color: 'primary.main', flexShrink: 0 }} />
                )}
                <ListItemText
                  primary={
                    type === 'domain'
                      ? item.companyName || item.domain
                      : item.name
                  }
                  secondary={
                    <Box component="span" sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                      <Typography component="span" variant="caption" color="text.secondary">
                        {type === 'domain' ? item.domain : item.company || 'No company'}
                      </Typography>
                      {phone && (
                        <Typography component="span" variant="caption" color="text.secondary">
                          {`· ${phone}`}
                        </Typography>
                      )}
                      {type === 'contact' && item.searchMethod && (
                        <Chip label={item.searchMethod} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem' }} />
                      )}
                      {item.searchedAt && (
                        <Typography component="span" variant="caption" color="text.disabled">
                          · {formatRelativeTime(item.searchedAt)}
                        </Typography>
                      )}
                    </Box>
                  }
                  primaryTypographyProps={{ noWrap: true }}
                />
              </ListItemButton>
            </ListItem>
          </React.Fragment>
          );
        })}
      </List>

      {effectivePageSize && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            mt: 1,
            px: 0.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Rows
            </Typography>
            <FormControl size="small">
              <Select
                value={rowsPerPage}
                onChange={handlePageSizeChange}
                SelectDisplayProps={{ 'aria-label': `${title} rows per page` }}
                sx={{ minWidth: 72, '& .MuiSelect-select': { py: 0.5 } }}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary">
              {startIndex + 1}–{startIndex + visibleItems.length} of {orderedItems.length}
            </Typography>
          </Box>
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(_, value) => setPage(value)}
              color="primary"
              size="small"
              siblingCount={0}
              aria-label={`${title} pages`}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default SearchHistoryPanel;
